import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/dates";
import { parseTimeInput, roundUpBilling, fmtDecimalHours, fmtHM, fmtMoney, todayIso } from "@/lib/time";
import { DateInput } from "@/components/DateInput";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/i18n";

const STATUS_BADGE = {
  Unbilled: "bg-amber-50 text-amber-800 border-amber-200",
  Billed: "bg-green-50 text-green-700 border-green-200",
};

const EXPENSE_CATEGORIES = [
  "Court fee", "Bailiff", "Translation", "Expert", "Travel", "Courier/Postage", "Registry fee", "Other",
];

function YesNoSelect({ value, onChange, testid, label }) {
  const { t } = useT();
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="mt-1.5">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="Yes" data-testid={`${testid}-yes`}>{t("val.Yes")}</SelectItem>
          <SelectItem value="No" data-testid={`${testid}-no`}>{t("val.No")}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function StatusSelect({ value, onChange, testid }) {
  const { t } = useT();
  return (
    <div>
      <Label>{t("time.billing_status")}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="mt-1.5">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="Unbilled" data-testid={`${testid}-unbilled`}>{t("val.Unbilled")}</SelectItem>
          <SelectItem value="Billed" data-testid={`${testid}-billed`}>{t("val.Billed")}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

export function ConfirmDelete({ open, onOpenChange, onConfirm, body, testid }) {
  const { t } = useT();
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid={testid}>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("common.delete")}</AlertDialogTitle>
          <AlertDialogDescription>{body}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testid}-cancel`}>{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            data-testid={`${testid}-confirm`}
            onClick={onConfirm}
            className="bg-red-600 text-white hover:bg-red-700"
          >
            {t("common.delete")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EntryRow({ children, testid, onEdit, onDelete, kind, id, lockedInvoice, draftInvoice }) {
  const { t } = useT();
  const rowId = id ?? testid.replace(new RegExp(`^${kind}-row-`), "");
  const locked = Boolean(lockedInvoice);
  return (
    <div
      data-testid={testid}
      className="bg-white border border-slate-200 border-l-4 border-l-slate-300 rounded-md px-5 py-4"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          {children}
          {locked && (
            <div className="mt-2">
              <Link
                to={`/invoices/${lockedInvoice.id}`}
                data-testid={`${kind}-invoice-link-${rowId}`}
                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border bg-green-50 text-green-700 border-green-200 hover:underline"
              >
                {t("entry.in_invoice", { number: lockedInvoice.number })}
              </Link>
            </div>
          )}
          {!locked && draftInvoice && (
            <div className="mt-2 text-xs text-amber-700" data-testid={`${kind}-draft-note-${rowId}`}>
              {t("entry.in_draft", { number: draftInvoice.number })}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            data-testid={`${kind}-edit-${rowId}`}
            onClick={onEdit}
            disabled={locked}
            title={locked ? t("entry.billed_locked") : t("common.edit")}
            className="p-1.5 rounded-md border border-slate-200 text-slate-500 hover:text-slate-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-slate-500"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            data-testid={`${kind}-delete-${rowId}`}
            onClick={onDelete}
            disabled={locked}
            title={locked ? t("entry.billed_locked") : t("common.delete")}
            className="p-1.5 rounded-md border border-slate-200 text-slate-500 hover:text-red-700 hover:border-red-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-slate-500 disabled:hover:border-slate-200"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Time ----------------
export function TimeSection({ matter, onChanged }) {
  const { t } = useT();
  const [entries, setEntries] = useState(null);
  const [dialog, setDialog] = useState({ open: false, item: null });
  const [toDelete, setToDelete] = useState(null);

  const load = () => {
    api.get(`/matters/${matter.id}/time-entries`).then((r) => setEntries(r.data)).catch(() => {});
  };
  useEffect(load, [matter.id]);

  const confirmDelete = async () => {
    const entry = toDelete;
    setToDelete(null);
    try {
      await api.delete(`/time-entries/${entry.id}`);
      load();
      onChanged();
      toast.success(t("time.deleted"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <section data-testid="time-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-serif text-xl text-slate-800">
          {t("time.title")} <span className="text-sm text-slate-400 font-sans">({entries?.length ?? 0})</span>
        </h2>
        <Button
          size="sm"
          data-testid="add-time-entry-button"
          onClick={() => setDialog({ open: true, item: null })}
          className="bg-slate-900 hover:bg-slate-800"
        >
          <Plus className="h-4 w-4 mr-1" /> {t("time.add_time")}
        </Button>
      </div>
      <div className="space-y-2.5">
        {entries === null ? (
          <div className="text-sm text-slate-500">{t("common.loading")}</div>
        ) : entries.length === 0 ? (
          <div className="text-sm text-slate-500" data-testid="no-time-entries">
            {t("time.none")}
          </div>
        ) : (
          entries.map((e) => (
            <EntryRow
              key={e.id}
              testid={`time-entry-row-${e.id}`}
              id={e.id}
              kind="time-entry"
              lockedInvoice={e.billed_invoice_id ? { id: e.billed_invoice_id, number: e.billed_invoice_number } : null}
              draftInvoice={e.allocated_invoice_id ? { id: e.allocated_invoice_id, number: e.allocated_invoice_number } : null}
              onEdit={() => setDialog({ open: true, item: e })}
              onDelete={() => setToDelete(e)}
            >
              <div className="text-sm font-semibold text-slate-800">{e.description}</div>
              <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-slate-500">
                <span className="font-medium text-slate-700">{formatDate(e.date)}</span>
                {(e.billing_type || "Hourly") !== "Hourly" && (
                  <span data-testid={`time-entry-type-${e.id}`} className="px-2 py-0.5 rounded-full border bg-violet-50 text-violet-800 border-violet-200">
                    {t(`val.${e.billing_type}`)}
                  </span>
                )}
                {(e.billing_type || "Hourly") === "Fixed Fee" ? (
                  <>
                    {e.actual_minutes > 0 && <span>{t("time.actual")} {fmtHM(e.actual_minutes)}</span>}
                    <span className="font-semibold text-slate-800" data-testid={`time-entry-amount-${e.id}`}>
                      {fmtMoney(e.amount)}
                    </span>
                    {e.vat_rate != null && <span>{t("time.vat_short", { rate: e.vat_rate })}</span>}
                  </>
                ) : (e.billing_type || "Hourly") === "Non-billable" ? (
                  <>
                    <span>{t("time.actual")} {fmtHM(e.actual_minutes)}</span>
                    <span className="font-semibold text-slate-800" data-testid={`time-entry-amount-${e.id}`}>
                      {fmtMoney(e.amount)}
                    </span>
                  </>
                ) : (
                  <>
                    <span>{t("time.actual")} {fmtHM(e.actual_minutes)}</span>
                    <span className="font-semibold text-slate-700">{t("time.billable")} {fmtDecimalHours(e.billable_minutes)}</span>
                    <span>{fmtMoney(e.hourly_rate)} {t("time.per_hour")}</span>
                    <span className="font-semibold text-slate-800" data-testid={`time-entry-amount-${e.id}`}>
                      {fmtMoney(e.amount)}
                    </span>
                  </>
                )}
                {(e.billing_type || "Hourly") === "Hourly" && (
                  <span className={`px-2 py-0.5 rounded-full border ${e.billable ? "bg-sky-50 text-sky-800 border-sky-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                    {e.billable ? t("time.billable_yes") : t("time.billable_no")}
                  </span>
                )}
                <span className={`px-2 py-0.5 rounded-full border ${STATUS_BADGE[e.billing_status] || ""}`}>
                  {t(`val.${e.billing_status}`)}
                </span>
              </div>
              {e.notes && <div className="text-xs text-slate-500 mt-1.5">{e.notes}</div>}
            </EntryRow>
          ))
        )}
      </div>
      <TimeEntryDialog
        open={dialog.open}
        onOpenChange={(o) => setDialog({ open: o, item: null })}
        matter={matter}
        initial={dialog.item}
        onSaved={() => {
          setDialog({ open: false, item: null });
          load();
          onChanged();
          toast.success(t(dialog.item ? "time.updated" : "time.added"));
        }}
      />
      <ConfirmDelete
        open={Boolean(toDelete)}
        onOpenChange={(o) => !o && setToDelete(null)}
        onConfirm={confirmDelete}
        testid="time-delete-confirm"
        body={toDelete
          ? t("time.delete_confirm") +
            (toDelete.allocated_invoice_number
              ? t("entry.draft_usage_delete", { number: toDelete.allocated_invoice_number })
              : "")
          : ""}
      />
    </section>
  );
}

export function TimeEntryDialog({ open, onOpenChange, matter, initial, onSaved, matters }) {
  const { t } = useT();
  const isEdit = Boolean(initial);
  const [matterId, setMatterId] = useState("");
  const [fetchedMatters, setFetchedMatters] = useState([]);
  const [date, setDate] = useState(todayIso());
  const [description, setDescription] = useState("");
  const [billingType, setBillingType] = useState("Hourly");
  const [timeText, setTimeText] = useState("");
  const [rateText, setRateText] = useState("");
  const [fixedAmountText, setFixedAmountText] = useState("");
  const [vatRateText, setVatRateText] = useState("");
  const [defaultVat, setDefaultVat] = useState(21);
  const [billable, setBillable] = useState("Yes");
  const [status, setStatus] = useState("Unbilled");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const matterOptions = matters || fetchedMatters;
  const resolvedMatter = (matterId && matterOptions.find((m) => m.id === matterId)) || matter || null;

  useEffect(() => {
    if (open && isEdit && !matters) {
      api.get("/matters").then((r) => setFetchedMatters(r.data)).catch(() => {});
    }
  }, [open, isEdit, matters]);

  useEffect(() => {
    if (open) {
      api.get("/settings").then((r) => setDefaultVat(Number(r.data?.default_vat_rate ?? 21))).catch(() => {});
      setMatterId(initial?.matter_id || "");
      setDate(initial?.date || todayIso());
      setDescription(initial?.description || "");
      setBillingType(initial?.billing_type || matter?.billing_type || "Hourly");
      setTimeText(initial ? fmtHM(initial.actual_minutes) : "");
      setRateText(
        initial ? String(initial.hourly_rate ?? "") : String(matter?.default_hourly_rate ?? "")
      );
      setFixedAmountText(initial?.fixed_amount != null ? String(initial.fixed_amount) : "");
      setVatRateText(initial?.vat_rate != null ? String(initial.vat_rate) : "");
      setBillable(initial ? (initial.billable ? "Yes" : "No") : "Yes");
      setStatus(initial?.billing_status || "Unbilled");
      setNotes(initial?.notes || "");
      setError("");
    }
  }, [open, initial, matter]);

  useEffect(() => {
    if (open && !matter && !isEdit && resolvedMatter) {
      setRateText(String(resolvedMatter.default_hourly_rate ?? ""));
      setBillingType(resolvedMatter.billing_type || "Hourly");
    }
  }, [matterId]);

  const isHourly = billingType === "Hourly";
  const isFixed = billingType === "Fixed Fee";

  const changeBillingType = (v) => {
    setBillingType(v);
    if (v === "Fixed Fee" && vatRateText === "") setVatRateText(String(defaultVat));
  };

  useEffect(() => {
    if (billingType === "Fixed Fee" && vatRateText === "") setVatRateText(String(defaultVat));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultVat]);
  const minutes = parseTimeInput(timeText);
  const billableMin = minutes ? roundUpBilling(minutes) : null;
  const rate = rateText === "" ? null : Number(rateText);
  const fixedAmount = fixedAmountText === "" ? null : Number(fixedAmountText);
  const vatRate = vatRateText === "" ? null : Number(vatRateText);
  const amountPreview =
    billableMin !== null && rate !== null && !isNaN(rate) && isHourly && billable === "Yes"
      ? (billableMin / 60) * rate
      : 0;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!date) {
      setError(t("time.invalid_date"));
      return;
    }
    if (!resolvedMatter) {
      setError(t("timer.select_first"));
      return;
    }
    if (isFixed) {
      if (fixedAmount === null || isNaN(fixedAmount) || fixedAmount < 0) {
        setError(t("time.fixed_required"));
        return;
      }
      if (timeText.trim() && (!minutes || minutes < 1)) {
        setError(t("time.invalid_actual"));
        return;
      }
      if (vatRateText !== "" && (isNaN(vatRate) || vatRate < 0 || vatRate > 100)) {
        setError(t("time.invalid_vat"));
        return;
      }
    } else if (!minutes || minutes < 1) {
      setError(t("time.invalid_actual"));
      return;
    }
    if (isHourly && rateText !== "" && (isNaN(rate) || rate < 0)) {
      setError(t("time.invalid_rate"));
      return;
    }
    setSaving(true);
    const payload = {
      date,
      description,
      matter_id: resolvedMatter.id,
      actual_minutes: isFixed ? (minutes ?? 0) : minutes,
      hourly_rate: rate,
      billable: isFixed ? true : billable === "Yes",
      billing_status: status,
      notes,
      billing_type: billingType,
      fixed_amount: isFixed ? fixedAmount : null,
      vat_rate: isFixed ? (vatRate ?? defaultVat) : null,
    };
    try {
      if (isEdit) await api.put(`/time-entries/${initial.id}`, payload);
      else await api.post(`/matters/${resolvedMatter.id}/time-entries`, payload);
      onSaved();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="time-entry-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">{t(isEdit ? "time.dialog_edit" : "time.dialog_new")}</DialogTitle>
          <DialogDescription className="sr-only">Time entry form</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="time-entry-form">
          {error && (
            <div data-testid="time-entry-form-error" role="alert" className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          {isEdit && initial?.allocated_invoice_number && (
            <div data-testid="time-entry-draft-warning" className="text-xs bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-amber-800">
              {t("entry.draft_usage_edit", { number: initial.allocated_invoice_number })}
            </div>
          )}
          {(isEdit || !matter) && matterOptions.length > 0 && (
            <div>
              <Label>{t("timer.matter")} <span className="text-red-600">*</span></Label>
              <Select value={matterId} onValueChange={setMatterId}>
                <SelectTrigger data-testid="time-entry-matter-select" className="mt-1.5">
                  <SelectValue placeholder={t("timer.select_matter")} />
                </SelectTrigger>
                <SelectContent>
                  {matterOptions.map((m) => (
                    <SelectItem key={m.id} value={m.id} data-testid={`time-entry-matter-option-${m.internal_ref || m.id}`}>
                      {m.name}{m.internal_ref ? ` (${m.internal_ref})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>{t("form.billing_type")} <span className="text-red-600">*</span></Label>
            <Select value={billingType} onValueChange={changeBillingType}>
              <SelectTrigger data-testid="time-entry-billing-type" className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Hourly" data-testid="time-entry-billing-type-hourly">{t("val.Hourly")}</SelectItem>
                <SelectItem value="Fixed Fee" data-testid="time-entry-billing-type-fixed">{t("val.Fixed Fee")}</SelectItem>
                <SelectItem value="Non-billable" data-testid="time-entry-billing-type-nonbillable">{t("val.Non-billable")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t("common.date")} <span className="text-red-600">*</span></Label>
              <div className="mt-1.5">
                <DateInput testid="time-entry-date-input" value={date} onChange={setDate} />
              </div>
            </div>
            <div>
              <Label htmlFor="te-time">
                {isFixed ? t("time.actual_optional") : t("time.actual_label")}{" "}
                {!isFixed && <span className="text-red-600">*</span>}
              </Label>
              <Input
                id="te-time"
                data-testid="time-entry-actual-input"
                value={timeText}
                onChange={(e) => setTimeText(e.target.value)}
                placeholder={t("time.actual_placeholder")}
                className="mt-1.5"
                autoComplete="off"
              />
              <div className="text-[11px] text-slate-400 mt-1">
                {isFixed ? t("time.actual_optional_hint") : t("time.actual_hint")}
              </div>
            </div>
          </div>
          <div>
            <Label htmlFor="te-desc">{t("common.description")} <span className="text-red-600">*</span></Label>
            <Input
              id="te-desc"
              data-testid="time-entry-description"
              required
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1.5"
            />
          </div>
          {isFixed ? (
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="te-fixed">{t("time.fixed_amount")} <span className="text-red-600">*</span></Label>
                <Input
                  id="te-fixed"
                  data-testid="time-entry-fixed-amount-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={fixedAmountText}
                  onChange={(e) => setFixedAmountText(e.target.value)}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="te-vat">{t("time.vat_rate")}</Label>
                <Input
                  id="te-vat"
                  data-testid="time-entry-vat-rate-input"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={vatRateText}
                  onChange={(e) => setVatRateText(e.target.value)}
                  placeholder={String(defaultVat)}
                  className="mt-1.5"
                />
              </div>
              <StatusSelect value={status} onChange={setStatus} testid="time-entry-status" />
            </div>
          ) : isHourly ? (
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="te-rate">{t("time.rate")}</Label>
                <Input
                  id="te-rate"
                  data-testid="time-entry-rate-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={rateText}
                  onChange={(e) => setRateText(e.target.value)}
                  className="mt-1.5"
                />
              </div>
              <YesNoSelect value={billable} onChange={setBillable} testid="time-entry-billable" label={t("time.billable")} />
              <StatusSelect value={status} onChange={setStatus} testid="time-entry-status" />
            </div>
          ) : (
            <StatusSelect value={status} onChange={setStatus} testid="time-entry-status" />
          )}
          {isHourly && billableMin !== null && (
            <div
              data-testid="time-entry-preview"
              className="text-xs bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-slate-600"
            >
              {t("time.preview_billable")}: <span className="font-semibold">{fmtDecimalHours(billableMin)}</span>
              {" · "}{t("time.preview_amount")}: <span className="font-semibold">{fmtMoney(amountPreview)}</span>
            </div>
          )}
          {isFixed && fixedAmount !== null && !isNaN(fixedAmount) && (
            <div
              data-testid="time-entry-preview"
              className="text-xs bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-slate-600"
            >
              {t("time.preview_amount")}: <span className="font-semibold">{fmtMoney(fixedAmount)}</span>
              <span className="block mt-1 text-slate-500">{t("time.fixed_note")}</span>
            </div>
          )}
          <div>
            <Label htmlFor="te-notes">{t("common.notes")}</Label>
            <Textarea
              id="te-notes"
              data-testid="time-entry-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1.5"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" data-testid="time-entry-cancel" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" data-testid="time-entry-submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800">
              {saving ? t("common.saving") : isEdit ? t("form.save_changes") : t("time.add_entry")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Expenses ----------------
export function ExpenseSection({ matter, onChanged }) {
  const { t } = useT();
  const [entries, setEntries] = useState(null);
  const [dialog, setDialog] = useState({ open: false, item: null });

  const load = () => {
    api.get(`/matters/${matter.id}/expenses`).then((r) => setEntries(r.data)).catch(() => {});
  };
  useEffect(load, [matter.id]);

  const [toDelete, setToDelete] = useState(null);

  const confirmDelete = async () => {
    const entry = toDelete;
    setToDelete(null);
    try {
      await api.delete(`/expenses/${entry.id}`);
      load();
      onChanged();
      toast.success(t("expenses.deleted"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <section data-testid="expenses-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-serif text-xl text-slate-800">
          {t("expenses.title")} <span className="text-sm text-slate-400 font-sans">({entries?.length ?? 0})</span>
        </h2>
        <Button
          size="sm"
          data-testid="add-expense-button"
          onClick={() => setDialog({ open: true, item: null })}
          className="bg-slate-900 hover:bg-slate-800"
        >
          <Plus className="h-4 w-4 mr-1" /> {t("expenses.add")}
        </Button>
      </div>
      <div className="space-y-2.5">
        {entries === null ? (
          <div className="text-sm text-slate-500">{t("common.loading")}</div>
        ) : entries.length === 0 ? (
          <div className="text-sm text-slate-500" data-testid="no-expenses">{t("expenses.none")}</div>
        ) : (
          entries.map((x) => (
            <EntryRow
              key={x.id}
              testid={`expense-row-${x.id}`}
              id={x.id}
              kind="expense"
              lockedInvoice={x.billed_invoice_id ? { id: x.billed_invoice_id, number: x.billed_invoice_number } : null}
              draftInvoice={x.allocated_invoice_id ? { id: x.allocated_invoice_id, number: x.allocated_invoice_number } : null}
              onEdit={() => setDialog({ open: true, item: x })}
              onDelete={() => setToDelete(x)}
            >
              <div className="text-sm font-semibold text-slate-800">{x.description}</div>
              <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-slate-500">
                <span className="font-medium text-slate-700">{formatDate(x.date)}</span>
                <span className="px-2 py-0.5 rounded-full border bg-slate-100 text-slate-600 border-slate-200">
                  {x.category === "Other" && x.category_other ? x.category_other : t(`val.${x.category}`)}
                </span>
                <span className="font-semibold text-slate-800" data-testid={`expense-amount-${x.id}`}>
                  {fmtMoney(x.amount)}
                </span>
                {x.vat != null && <span>VAT {fmtMoney(x.vat)}</span>}
                <span className={`px-2 py-0.5 rounded-full border ${x.billable_to_client ? "bg-sky-50 text-sky-800 border-sky-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                  {x.billable_to_client ? t("expenses.billable_yes") : t("expenses.billable_no")}
                </span>
                <span className={`px-2 py-0.5 rounded-full border ${STATUS_BADGE[x.billing_status] || ""}`}>
                  {t(`val.${x.billing_status}`)}
                </span>
                {x.dropbox_ref && (
                  <a
                    href={/^https?:\/\//i.test(x.dropbox_ref) ? x.dropbox_ref : `https://${x.dropbox_ref}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`expense-dropbox-${x.id}`}
                    className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" /> {t("expenses.dropbox_link")}
                  </a>
                )}
              </div>
              {x.notes && <div className="text-xs text-slate-500 mt-1.5">{x.notes}</div>}
            </EntryRow>
          ))
        )}
      </div>
      <ExpenseDialog
        open={dialog.open}
        onOpenChange={(o) => setDialog({ open: o, item: null })}
        matter={matter}
        initial={dialog.item}
        onSaved={() => {
          setDialog({ open: false, item: null });
          load();
          onChanged();
          toast.success(t(dialog.item ? "expenses.updated" : "expenses.added"));
        }}
      />
      <ConfirmDelete
        open={Boolean(toDelete)}
        onOpenChange={(o) => !o && setToDelete(null)}
        onConfirm={confirmDelete}
        testid="expense-delete-confirm"
        body={toDelete
          ? t("expenses.delete_confirm") +
            (toDelete.allocated_invoice_number
              ? t("entry.draft_usage_delete", { number: toDelete.allocated_invoice_number })
              : "")
          : ""}
      />
    </section>
  );
}

function ExpenseDialog({ open, onOpenChange, matter, initial, onSaved }) {
  const { t } = useT();
  const isEdit = Boolean(initial);
  const [date, setDate] = useState(todayIso());
  const [description, setDescription] = useState("");
  const [matterId, setMatterId] = useState("");
  const [fetchedMatters, setFetchedMatters] = useState([]);
  const [category, setCategory] = useState("Court fee");
  const [categoryOther, setCategoryOther] = useState("");
  const [amountText, setAmountText] = useState("");
  const [vatText, setVatText] = useState("");
  const [billable, setBillable] = useState("Yes");
  const [status, setStatus] = useState("Unbilled");
  const [notes, setNotes] = useState("");
  const [dropboxRef, setDropboxRef] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDate(initial?.date || todayIso());
      setDescription(initial?.description || "");
      setCategory(initial?.category || "Court fee");
      setCategoryOther(initial?.category_other || "");
      setAmountText(initial ? String(initial.amount) : "");
      setVatText(initial && initial.vat != null ? String(initial.vat) : "");
      setBillable(initial ? (initial.billable_to_client ? "Yes" : "No") : "Yes");
      setStatus(initial?.billing_status || "Unbilled");
      setNotes(initial?.notes || "");
      setDropboxRef(initial?.dropbox_ref || "");
      setMatterId(initial?.matter_id || "");
      setError("");
    }
  }, [open, initial]);

  useEffect(() => {
    if (open && isEdit) {
      api.get("/matters").then((r) => setFetchedMatters(r.data)).catch(() => {});
    }
  }, [open, isEdit]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!date) {
      setError(t("time.invalid_date"));
      return;
    }
    const amount = Number(amountText);
    if (amountText === "" || isNaN(amount) || amount < 0) {
      setError(t("expenses.invalid_amount"));
      return;
    }
    const vat = vatText === "" ? null : Number(vatText);
    if (vatText !== "" && (isNaN(vat) || vat < 0)) {
      setError(t("expenses.invalid_vat"));
      return;
    }
    setSaving(true);
    const payload = {
      date,
      description,
      matter_id: matterId || matter.id,
      category,
      category_other: category === "Other" ? categoryOther : "",
      amount,
      vat,
      billable_to_client: billable === "Yes",
      billing_status: status,
      notes,
      dropbox_ref: dropboxRef,
    };
    try {
      if (isEdit) await api.put(`/expenses/${initial.id}`, payload);
      else await api.post(`/matters/${matter.id}/expenses`, payload);
      onSaved();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="expense-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">{t(isEdit ? "expenses.dialog_edit" : "expenses.dialog_new")}</DialogTitle>
          <DialogDescription className="sr-only">Expense entry form</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="expense-form">
          {error && (
            <div data-testid="expense-form-error" role="alert" className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          {isEdit && (
            <div>
              <Label>{t("timer.matter")} <span className="text-red-600">*</span></Label>
              <Select value={matterId} onValueChange={setMatterId}>
                <SelectTrigger data-testid="expense-matter-select" className="mt-1.5">
                  <SelectValue placeholder={t("timer.select_matter")} />
                </SelectTrigger>
                <SelectContent>
                  {fetchedMatters.map((m) => (
                    <SelectItem key={m.id} value={m.id} data-testid={`expense-matter-option-${m.internal_ref || m.id}`}>
                      {m.name}{m.internal_ref ? ` (${m.internal_ref})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {isEdit && initial?.allocated_invoice_number && (
            <div data-testid="expense-draft-warning" className="text-xs bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-amber-800">
              {t("entry.draft_usage_edit", { number: initial.allocated_invoice_number })}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t("common.date")} <span className="text-red-600">*</span></Label>
              <div className="mt-1.5">
                <DateInput testid="expense-date-input" value={date} onChange={setDate} />
              </div>
            </div>
            <div>
              <Label>{t("expenses.category")}</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="expense-category" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPENSE_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`expense-category-option-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {t(`val.${c}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {category === "Other" && (
            <div>
              <Label htmlFor="ex-cat-other">{t("expenses.category_other")}</Label>
              <Input
                id="ex-cat-other"
                data-testid="expense-category-other"
                value={categoryOther}
                onChange={(e) => setCategoryOther(e.target.value)}
                className="mt-1.5"
              />
            </div>
          )}
          <div>
            <Label htmlFor="ex-desc">{t("common.description")} <span className="text-red-600">*</span></Label>
            <Input
              id="ex-desc"
              data-testid="expense-description"
              required
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ex-amount">{t("expenses.amount")} <span className="text-red-600">*</span></Label>
              <Input
                id="ex-amount"
                data-testid="expense-amount-input"
                type="number"
                min="0"
                step="0.01"
                value={amountText}
                onChange={(e) => setAmountText(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="ex-vat">{t("expenses.vat")}</Label>
              <Input
                id="ex-vat"
                data-testid="expense-vat-input"
                type="number"
                min="0"
                step="0.01"
                value={vatText}
                onChange={(e) => setVatText(e.target.value)}
                className="mt-1.5"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <YesNoSelect value={billable} onChange={setBillable} testid="expense-billable" label={t("expenses.billable_to_client")} />
            <StatusSelect value={status} onChange={setStatus} testid="expense-status" />
          </div>
          <div>
            <Label htmlFor="ex-dropbox">{t("expenses.dropbox_ref")}</Label>
            <Input
              id="ex-dropbox"
              data-testid="expense-dropbox-input"
              value={dropboxRef}
              onChange={(e) => setDropboxRef(e.target.value)}
              placeholder="https://www.dropbox.com/..."
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="ex-notes">{t("common.notes")}</Label>
            <Textarea
              id="ex-notes"
              data-testid="expense-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1.5"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" data-testid="expense-cancel" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" data-testid="expense-submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800">
              {saving ? t("common.saving") : isEdit ? t("form.save_changes") : t("expenses.add")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
